import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PhoneOff, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { hoursBetween, dayScheduleFor, tourTitle } from "@/lib/fields";
import { api } from "@/lib/api";

export const TYPE_POINTS = { "Sick": 1, "Personal": 1, "FMLA Self": 0, "FMLA Other": 0, "Bereavement": 0, "Jury Duty": 0, "Late": 0.5, "Vacation": 0, "ADO": 0, "Giveaway": 0, "Trade": 0 };
export const CALLOUT_TYPES = Object.keys(TYPE_POINTS);
const TIME_OFF_TYPES = ["Vacation", "ADO"];
const COVERAGE_TYPES = ["Giveaway", "Trade"];
const CALL_OUT_TYPES = CALLOUT_TYPES.filter((t) => !TIME_OFF_TYPES.includes(t) && !COVERAGE_TYPES.includes(t));
export const PAY_TYPES = ["Sick", "Vacation", "Unpaid"];
const todayStr = () => { const d = new Date(); return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`; };
const n2 = (v) => (Math.round((Number(v) || 0) * 100) / 100);

const parseMdy = (s) => {
  const m = String(s || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  let [, mo, da, yr] = m;
  yr = yr.length === 2 ? "20" + yr : yr;
  const d = new Date(Number(yr), Number(mo) - 1, Number(da));
  d.setHours(0, 0, 0, 0);
  return isNaN(d.getTime()) ? null : d;
};
const isoToMdy = (iso) => { const m = String(iso || "").match(/(\d{4})-(\d{2})-(\d{2})/); return m ? `${+m[2]}/${+m[3]}/${m[1]}` : ""; };
const mdyToIso = (mdy) => { const d = parseMdy(mdy); return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : ""; };
const dayKey = (s) => { const d = parseMdy(s); return d ? `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}` : String(s || ""); };
// Call-out button only makes sense for a shift/tour happening today.
export const isTodayShift = (mdy) => {
  const d = parseMdy(mdy);
  if (!d) return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / 86400000);
  return diff === 0;
};

// Create/edit a call-out. `source` (optional) = {kind:'tour'|'shift', id, label, date, multiDay}.
export const CallOutDialog = ({ open, onOpenChange, initial, source, onSaved }) => {
  const [type, setType] = useState("Sick");
  const [points, setPoints] = useState(1);
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [minutes, setMinutes] = useState("");
  const [shiftHours, setShiftHours] = useState("");
  const [payType, setPayType] = useState("Sick");
  const [hoursPaid, setHoursPaid] = useState("");
  const [tradedWith, setTradedWith] = useState("");
  const [tradeDate, setTradeDate] = useState("");
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form"); // form | remove
  const [createdId, setCreatedId] = useState(null);
  const [sched, setSched] = useState({ shifts: [], tours: [] });
  const [autoLabel, setAutoLabel] = useState(null);

  // Manual add: pull the schedule so picking a date can auto-load that day's shift.
  const manualLookup = !initial && !source?.id;
  useEffect(() => {
    if (!open || !manualLookup) return;
    Promise.all([api.listShifts().catch(() => []), api.listGuests().catch(() => [])])
      .then(([s, g]) => setSched({ shifts: Array.isArray(s) ? s : [], tours: Array.isArray(g) ? g : [] }));
  }, [open, manualLookup]);

  // Auto-load the schedule for the currently selected date once tours/shifts arrive.
  useEffect(() => {
    if (!open || !manualLookup || autoLabel || shiftHours !== "") return;
    const found = findScheduleFor(date);
    if (found) { setShiftHours(found.hours ? String(found.hours) : ""); setHoursPaid(found.hours ? String(found.hours) : ""); setAutoLabel(found.label); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sched]);

  const findScheduleFor = (mdy) => {
    const key = dayKey(mdy);
    const sh = (sched.shifts || []).find((s) => dayKey(s.date) === key);
    if (sh) return { hours: hoursBetween(sh.start_time, sh.end_time), label: sh.label || "Shift" };
    for (const t of sched.tours || []) {
      const dates = Array.isArray(t.tour_date) ? t.tour_date : (t.tour_date ? [t.tour_date] : []);
      if (dates.some((d) => dayKey(d) === key)) {
        const ds = dayScheduleFor(t, mdy);
        return { hours: hoursBetween(ds.start, ds.end), label: tourTitle(t) };
      }
    }
    return null;
  };

  const applyDate = (iso) => {
    const mdy = isoToMdy(iso);
    setDate(mdy);
    if (!manualLookup) return;
    const found = findScheduleFor(mdy);
    if (found) { setShiftHours(found.hours ? String(found.hours) : ""); setHoursPaid(found.hours ? String(found.hours) : ""); setAutoLabel(found.label); }
    else setAutoLabel(null);
  };

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setType(initial.type); setPoints(initial.points); setDate(initial.date || todayStr());
      setNote(initial.note || ""); setMinutes(initial.minutes_late ?? "");
      setShiftHours(initial.shift_hours != null ? String(initial.shift_hours) : "");
      setPayType(initial.pay_type || "Unpaid");
      setHoursPaid(initial.hours_paid != null ? String(initial.hours_paid) : "");
      setTradedWith(initial.traded_with || "");
      setTradeDate(initial.trade_date || "");
    } else {
      setType("Sick"); setPoints(1); setDate(source?.date || todayStr()); setNote(""); setMinutes("");
      setShiftHours(source?.hours != null ? String(source.hours) : "");
      setPayType("Sick"); setHoursPaid(source?.hours != null ? String(source.hours) : "");
      setTradedWith("");
      setTradeDate("");
    }
    setAutoLabel(null); setStage("form"); setCreatedId(null);
  }, [open, initial, source]);

  // Remember names used on past Giveaway/Trade entries so the "who" box can suggest them.
  useEffect(() => {
    if (!open) return;
    api.listCallouts().then((list) => {
      const names = [...new Set((Array.isArray(list) ? list : []).map((c) => (c.traded_with || "").trim()).filter(Boolean))].sort();
      setNameSuggestions(names);
    }).catch(() => {});
  }, [open]);

  const pickType = (t) => {
    setType(t); setPoints(TYPE_POINTS[t]);
    if (t !== "Late") setMinutes("");
    if (t === "Vacation") { setPayType("Vacation"); if (shiftHours === "") { setShiftHours("8"); setHoursPaid("8"); } }
    if (!COVERAGE_TYPES.includes(t)) setTradedWith("");
    if (t !== "Trade") setTradeDate("");
  };

  const isCoverage = COVERAGE_TYPES.includes(type);
  const hidePoints = isCoverage || TIME_OFF_TYPES.includes(type);
  const isLate = type === "Late";
  const isAdo = type === "ADO";
  const noHours = isLate || isAdo || type === "Trade";  // ADO per-day; Trade never has paid time off
  const isFmla = type.startsWith("FMLA");
  const sh = n2(shiftHours);
  const paidVal = payType === "Unpaid" ? 0 : (hoursPaid === "" ? 0 : Math.min(n2(hoursPaid), sh));
  const unpaidVal = n2(Math.max(sh - paidVal, 0));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        type, points, date, note,
        minutes_late: type === "Late" && minutes !== "" ? Number(minutes) : null,
        source_kind: source?.kind, source_id: source?.id, source_label: source?.label || initial?.source_label || autoLabel,
        shift_hours: noHours ? null : (shiftHours === "" ? 0 : sh),
        pay_type: noHours ? null : payType,
        hours_paid: noHours ? null : paidVal,
        traded_with: isCoverage ? (tradedWith.trim() || null) : null,
        trade_date: type === "Trade" ? (tradeDate || null) : null,
      };
      if (initial) {
        await api.updateCallout(initial.id, body);
      } else {
        const created = await api.createCallout(body);
        setCreatedId(created?.id || null);
      }
      toast.success(initial ? "Call-out updated" : "Call-out recorded");
      if (!initial && source?.id && type !== "Late") { setStage("remove"); setBusy(false); return; }
      onSaved?.(); onOpenChange(false);
    } catch { toast.error("Could not save call-out"); }
    finally { setBusy(false); }
  };

  const removeSource = async (doRemove) => {
    if (!doRemove || !createdId) { onSaved?.(); onOpenChange(false); return; }
    setBusy(true);
    try {
      await api.calloutRemoveSource(createdId, { kind: source.kind, source_id: source.id, date, multiDay: source.multiDay });
      toast.success("Removed from schedule");
      window.dispatchEvent(new Event("schedule-invalidate"));
    } catch { toast.error("Recorded, but couldn't remove from schedule"); }
    finally { setBusy(false); onSaved?.(); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88svh] w-[calc(100vw-1.5rem)] max-w-md flex-col gap-0 overflow-hidden border-zinc-800 bg-zinc-950 p-0" data-testid="callout-dialog">
        {stage === "form" ? (
          <>
            <div className="shrink-0 px-6 pt-6">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-zinc-100"><PhoneOff className="h-4 w-4 text-[#E0575B]" /> {initial ? "Edit entry" : "Record Time Off"}</DialogTitle>
              </DialogHeader>
              {source?.label && <p className="mt-1 text-xs text-zinc-500">For: <span className="text-zinc-300">{source.label}</span></p>}
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Date</Label>
                  <SingleDatePicker value={mdyToIso(date)} onChange={applyDate} testid="callout-date" className="w-full" placeholder="Pick a date" />
                  {autoLabel && <p className="mt-1 text-xs text-emerald-300/80" data-testid="callout-autoload">Loaded {autoLabel}{sh > 0 ? ` · ${sh}h` : ""}</p>}
                </div>
                {type === "Late" && (
                  <div className="w-28">
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Min. late</Label>
                    <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="15" className="border-zinc-800 bg-zinc-900" data-testid="callout-minutes" />
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Call out type</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {CALL_OUT_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => pickType(t)} data-testid={`callout-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`rounded-lg border px-2.5 py-2 text-xs font-500 transition-colors ${type === t ? "border-[#9B2226] bg-[#9B2226]/20 text-[#E0575B]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Time off</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {TIME_OFF_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => pickType(t)} data-testid={`callout-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`rounded-lg border px-2.5 py-2 text-xs font-500 transition-colors ${type === t ? "border-sky-500 bg-sky-500/15 text-sky-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Shift coverage</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {COVERAGE_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => pickType(t)} data-testid={`callout-type-${t.toLowerCase()}`}
                      className={`rounded-lg border px-2.5 py-2 text-xs font-500 transition-colors ${type === t ? "border-teal-500 bg-teal-500/15 text-teal-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{t}</button>
                  ))}
                </div>
              </div>
              {isCoverage && (
                <div data-testid="callout-coverage-who">
                  <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">{type === "Trade" ? "Trade with who?" : "Gave to?"}</Label>
                  <Input
                    value={tradedWith}
                    onChange={(e) => setTradedWith(e.target.value)}
                    list="callout-traded-names"
                    placeholder={type === "Trade" ? "Name of who you traded with" : "Name of who you gave it to"}
                    className="border-zinc-800 bg-zinc-900"
                    data-testid="callout-traded-with"
                  />
                  <datalist id="callout-traded-names">
                    {nameSuggestions.map((n) => <option key={n} value={n} />)}
                  </datalist>
                  {type === "Trade" && (
                    <div className="mt-3" data-testid="callout-trade-date-wrap">
                      <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Date traded for</Label>
                      <SingleDatePicker value={mdyToIso(tradeDate)} onChange={(iso) => setTradeDate(isoToMdy(iso))} testid="callout-trade-date" className="w-full" placeholder="Day you're working in return" />
                    </div>
                  )}
                </div>
              )}
              {!hidePoints && (
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Point value</Label>
                <div className="flex gap-1.5">
                  {[0, 0.5, 1].map((p) => (
                    <button key={p} type="button" onClick={() => setPoints(p)} data-testid={`callout-points-${p}`}
                      className={`flex-1 rounded-lg border py-2 text-sm font-700 transition-colors ${points === p ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{p}</button>
                  ))}
                </div>
              </div>
              )}
              {!noHours && (
                <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid="callout-pay-section">
                  <div className="flex items-end gap-3">
                    <div className="w-28">
                      <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Shift hours</Label>
                      <Input type="number" step="0.01" value={shiftHours} onChange={(e) => setShiftHours(e.target.value)} placeholder="0" className="border-zinc-800 bg-zinc-900" data-testid="callout-shift-hours" />
                    </div>
                    <div className="flex-1">
                      <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Pay type</Label>
                      <div className="flex gap-1.5">
                        {PAY_TYPES.map((p) => (
                          <button key={p} type="button" onClick={() => { setPayType(p); if (p !== "Unpaid" && hoursPaid === "") setHoursPaid(String(sh)); }} data-testid={`callout-paytype-${p.toLowerCase()}`}
                            className={`flex-1 rounded-lg border px-1 py-2 text-xs font-500 transition-colors ${payType === p ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>{p}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {payType !== "Unpaid" && (
                    <div className="flex items-end gap-3">
                      <div className="w-28">
                        <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">{payType} hours</Label>
                        <Input type="number" step="0.01" min="0" inputMode="decimal" value={hoursPaid} onChange={(e) => setHoursPaid(e.target.value)} className="border-zinc-800 bg-zinc-900" data-testid="callout-hours-paid" />
                      </div>
                      <p className="pb-2 text-xs text-zinc-400" data-testid="callout-pay-breakdown">
                        <span className="text-emerald-300">{n2(paidVal)}h {payType}</span> + <span className="text-zinc-300">{unpaidVal}h Unpaid</span> = {sh}h
                      </p>
                    </div>
                  )}
                  {payType === "Unpaid" && <p className="text-xs text-zinc-400" data-testid="callout-pay-breakdown">All {sh}h unpaid</p>}
                  {isFmla && sh > 0 && (
                    <p className="rounded-md bg-[#9B2226]/10 px-2.5 py-1.5 text-xs text-[#E0575B]" data-testid="callout-fmla-note">Deducts {sh}h from your FMLA bank (480h total).</p>
                  )}
                </div>
              )}
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Note (optional)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="border-zinc-800 bg-zinc-900" data-testid="callout-note" />
              </div>
            </div>
            <DialogFooter className="shrink-0 gap-2 border-t border-zinc-800 bg-zinc-950 px-6 py-3 pb-[max(0.875rem,env(safe-area-inset-bottom))]">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#9B2226] text-white hover:bg-[#7f1c1f]" data-testid="callout-save">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} {initial ? "Save" : "Record"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="px-6 py-6">
            <DialogHeader>
              <DialogTitle className="text-zinc-100">Remove from schedule?</DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-sm text-zinc-400">Recorded. Since you're off that day, do you want to remove {source?.multiDay ? "this day" : "this"} from your schedule?</p>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => removeSource(false)} disabled={busy} className="border-zinc-700 bg-transparent text-zinc-300" data-testid="callout-keep">Keep it</Button>
              <Button onClick={() => removeSource(true)} disabled={busy} className="bg-[#9B2226] text-white hover:bg-[#7f1c1f]" data-testid="callout-remove">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Remove
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Small trigger button for cards (Home / Calendar).
export const CallOutButton = ({ source, onSaved, className = "", testid }) => {
  const [open, setOpen] = useState(false);
  if (source?.date && !isTodayShift(source.date)) return null;
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={testid}
        className={`inline-flex items-center gap-1 rounded-md border border-[#9B2226]/40 bg-[#9B2226]/10 px-2 py-1 text-[11px] font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20 ${className}`}>
        <PhoneOff className="h-3 w-3" /> Call out
      </button>
      <CallOutDialog open={open} onOpenChange={setOpen} source={source} onSaved={onSaved} />
    </>
  );
};
